import { useEffect, useState } from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { usePermissions } from "../../hooks/usePermissions";
import { getStoreById } from "../../services/storesService";
import FullPageLoader from "./FullPageLoader";
import AccessRestricted from "./AccessRestricted";

// Guard da rota de detalhes da cotação do vendedor (deep link com ?storeId).
// Assume usuário autenticado, com telefone verificado e dados do /me carregados
// (AuthGuard pai).
//
// Regras:
// - Sem storeId  -> checagem padrão de grupo "Seller".
// - user.storeId === storeId          -> acesso liberado (é o vendedor da loja).
// - loja já possui outro vendedor      -> mensagem dedicada.
// - loja sem vendedor (usuário existente, não associado) -> acesso restrito.
const StoreSellerAccessGuard = () => {
  const { user } = useUser();
  const { isSeller, groups } = usePermissions();
  const [searchParams] = useSearchParams();
  const storeId = searchParams.get("storeId");
  const isStoreSeller = !!storeId && user?.storeId === storeId;
  const needsStore = !!storeId && !isStoreSeller;

  const [store, setStore] = useState(null);
  const [checking, setChecking] = useState(needsStore);

  useEffect(() => {
    if (!needsStore) return;
    let active = true;
    setChecking(true);
    getStoreById(storeId)
      .then((storeData) => active && setStore(storeData))
      .catch((error) => console.error("Error checking store seller access:", error))
      .finally(() => active && setChecking(false));
    return () => {
      active = false;
    };
  }, [needsStore, storeId]);

  // Sem storeId: comporta-se como o SellerGuard padrão
  if (!storeId) {
    return isSeller ? (
      <Outlet />
    ) : (
      <AccessRestricted debugInfo={`Grupos: ${JSON.stringify(groups)}`} />
    );
  }

  // É o vendedor desta loja
  if (isStoreSeller) {
    return <Outlet />;
  }

  if (checking) {
    return <FullPageLoader />;
  }

  // A loja já possui outro vendedor vinculado
  if (store?.hasSeller) {
    return (
      <AccessRestricted
        title="Loja já possui vendedor"
        badge="Acesso à Cotação"
        message="Esta loja já possui um vendedor vinculado. Apenas o vendedor responsável pode acessar esta cotação."
        secondaryMessage="Se você acredita que isto é um engano, entre em contato com o suporte."
      />
    );
  }

  // Loja sem vendedor, mas o usuário já existe e não está associado a esta loja
  return (
    <AccessRestricted
      message="Você não tem acesso a esta cotação."
      secondaryMessage="Se você é o vendedor desta loja, faça o cadastro através do link fornecido pela loja."
    />
  );
};

export default StoreSellerAccessGuard;
